import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ArticleDetail, ContentCard } from '@/lib/content/types'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { ArticleView } from '@/components/site/article-view'
import { articleJsonLd, field, summaryOf } from '@/components/site/article-meta'
import { getPost, relatedContent } from '@/lib/content/queries'
import { useLocale } from '@/i18n/use-locale'
import { common } from '@/i18n/common'

interface PostPageProps {
    item: ArticleDetail
    related: Array<ContentCard>
}

const PostPage: FC<PostPageProps> = ({ item, related }): ReactNode => {
    const locale = useLocale()
    const title = field(item, 'title', locale).value
    const description = summaryOf(item, locale).value || undefined
    const path = `/blog/${item.slug}`
    const author = item.author ? { name: item.author.name, jobTitle: field(item.author, 'role', locale).value || null } : null

    return (
        <Layout>
            <Seo
                title={title}
                description={description}
                path={path}
                type='article'
                ogImage={item.cover ?? undefined}
                breadcrumbs={[
                    { name: common[locale].nav.blog, path: '/blog' },
                    { name: title, path }
                ]}
                jsonLd={[articleJsonLd({
                    headline: title,
                    description,
                    path,
                    locale,
                    datePublished: item.published_at,
                    dateModified: item.updated_at,
                    image: item.cover,
                    author
                })]}
            />

            <ArticleView item={item} related={related} />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' })

export const getStaticProps: GetStaticProps<PostPageProps, { slug: string }> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    if (!slug) return { notFound: true, revalidate: 60 }

    let item: ArticleDetail | null = null

    try {
        item = await getPost(slug)
    }
    catch (error) {
        console.error(`Could not load blog post ${slug}`, error)
        throw error
    }

    if (!item) return { notFound: true, revalidate: 60 }

    let related: Array<ContentCard> = []

    try {
        related = await relatedContent({ type: 'post', id: item.id, topicId: item.topic_id }, 3)
    }
    catch (error) {
        console.error(`Could not load related content for blog post ${slug}`, error)
    }

    return { props: { item, related }, revalidate: 60 }
}

export default PostPage
